import { GroundRupture, updatePreSpawnEffect, sweepPreSpawnEffects } from "./effects/pre_spawn_effect"
import { stairTravelInRoom, stairTravelPose } from "game-common/stair_travel"
import { RenderPosition, smoothRenderPosition } from "../../../render_position"
import { GlintSubject } from "./effects/dropped_item_glint"
import { EntityVisuals } from "./entity_visuals"
import { renderEntityFrame } from "./entity_frame"
import { advanceProjectile } from "../../../projectile_playback"
import { OTHER_FLOOR_FEATURES, OBSERVATION_FEATURES, FloorRenderFeatures } from "./floor_render_features"
import { Mechanics } from "game-common/mechanics/mechanics"
import { ScalingParams } from "../../../client_util"
import { initializeEntityDepth } from "./entity_depth"
import { Container, Rectangle, Sprite, filters } from "pixi.js"
import type { ProjectedComposite } from "./roof_composite_overlap"
import { Coordinate, CrossFloorAim, Direction, EmberRosterRole, Entity, Light, LightsContainer, SceneRoomState, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { companionFloorOffset, insideHouse, TwoStoreyHouse } from "game-common/room/ember/two_storey_house"
import type { ClientRenderable } from "./entity_rendering_manager"
import { WorldOverlayItem, WorldOverlaySurface } from "./world_overlay_surface"
import { VisibilityFade } from "./visibility_fade"
import { isExteriorFloorFall } from "game-common/floor_fall"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { sceneFloorGroundDepth } from "./building_depth"
import { ViewFilteredContainer } from "./view_filtered_container"
import type { DrawnStorey } from "../../../building_levels"
import type { TileEffect } from "game-common/map/map"

interface PositionSample { ts: number; x: number; y: number; direction: Direction; angle: number }

/** Deeper storeys fall off the bottom of the view, beneath the nearer ones. */
export const MAX_VISIBLE_FLOORS_BELOW = 4

interface SceneMap {
    getCompositeSprites?(): Sprite[]
    getTileArt?(x: number, y: number, layerId: string): Sprite[]
    getArtworkVersion?(): number
    isRoofVisibleForPlacement?(): boolean
    overlapsRenderedMap?(bounds: Rectangle): boolean
    setProjectionFloor?(floor: number): void
    setViewEntity?(entity: Entity): void
    update(map: TileMapMetaProvider): void
    renderTileMap(): void
    queueMapDelta(deltas: SceneRoomState["mapDeltas"]): void
    flushMapDeltas?(): void
    tileEffect?(effect: TileEffect): void
    updateTileEffects?(): void
    rerender(): void
    destroy(): void
}

/** A passive view of the other simulation room, using the normal tile and entity artwork. */
export class SceneFloorRenderer {
    private roomId?: string
    private visuals = new EntityVisuals()
    private overlays?: WorldOverlaySurface
    private spawnEffects = new Map<string, GroundRupture>()
    private completedSpawns = new Set<string>()
    root?: Container
    map?: SceneMap
    provider?: TileMapMetaProvider
    private groundBlur?: InstanceType<typeof filters.BlurFilter>
    private groundShade?: InstanceType<typeof filters.ColorMatrixFilter>
    private groundBrightness?: number
    private clockOffset?: number
    // This floor's map drew it as the active room until a change of level (SceneRoomRenderer.exchange).
    // The whole map the server sends with the floor's first frame is the one already drawn.
    private adopted = false
    private visibilityFade = new VisibilityFade()
    private entities = new Map<string, { entity: Entity; sprite: ClientRenderable; overlay: WorldOverlayItem;
        samples: PositionSample[]; renderPosition?: RenderPosition; visualDirty?: boolean; bulletTs?: number; bulletStopped?: boolean }>()

    constructor(private surface: Container, private createMap: (root: Container, sky: Container) => SceneMap,
        private createEntity: (entity: Entity, provider?: TileMapMetaProvider) => ClientRenderable, private observation = false,
        private retainEntity?: (entity: Entity, sprite: ClientRenderable) => boolean,
        private getRole: (id: string) => EmberRosterRole | null = () => null,
        private features: Readonly<FloorRenderFeatures> = observation ? OBSERVATION_FEATURES : OTHER_FLOOR_FEATURES) {}

    clear(): void {
        this.spawnEffects.forEach(effect => effect.destroy())
        this.spawnEffects.clear()
        this.completedSpawns.clear()
        this.entities.forEach(({ entity, sprite, overlay }) => {
            if (!this.retainEntity?.(entity, sprite)) { sprite.cleanup?.(); sprite.destroy({ children: true }) }
            this.overlays?.remove(entity)
            overlay.destroy({ children: true })
        })
        this.entities.clear()
        this.map?.destroy()
        this.root?.parent?.removeChild(this.root)
        this.root?.destroy({ children: true })
        this.groundBlur?.destroy()
        this.groundBlur = undefined
        this.groundShade?.destroy()
        this.groundShade = undefined
        this.groundBrightness = undefined
        this.map = undefined
        this.root = undefined
        this.overlays = undefined
        this.provider = undefined
        this.roomId = undefined
        this.clockOffset = undefined
        this.visibilityFade.reset()
    }

    update(frames: SceneRoomState[], active?: TwoStoreyHouse): void {
        const frame = frames[0]
        if ((!active && !this.observation) || !frame) { this.clear(); return }
        if (this.roomId !== frame.roomId) this.clear()
        if (!this.root) {
            if (!frame.tilemaps || !frame.tilemapMeta) return
            this.roomId = frame.roomId
            const sky = this.createRoot(frame.tilemaps?.twoStoreyHouse?.floor === 0)
            this.map = this.createMap(this.root, sky)
        }
        const adopted = this.adopted
        this.adopted = false
        if (frame.tilemaps && frame.tilemapMeta && !adopted) {
            // Full snapshots also arrive after roof/floor deletions. Keep the container,
            // fade and entity playback buffers when refreshing the same passive room.
            this.provider = new TileMapMetaProvider(frame.roomId, frame.tilemapMeta, frame.tilemaps, undefined)
            this.map.setProjectionFloor?.(this.observation ? this.provider.twoStoreyHouse?.floor : active.floor)
            this.map.update(this.provider)
            if (this.root.visible || !this.map.flushMapDeltas) this.map.renderTileMap()
        }
        if (!this.root || !this.provider) return
        if (this.observation) active = this.provider.getHousePresentation()
        if (!active) return
        const now = Date.now()
        const timestamp = frame.serverTs ?? now - (this.clockOffset ?? 0)
        const offset = now - timestamp
        this.clockOffset = Math.min(this.clockOffset ?? offset, offset)
        this.root.y = this.observation ? 0 : companionFloorOffset(active, this.provider.twoStoreyHouse)
        const floor = this.provider.twoStoreyHouse.floor
        this.root.zIndex = this.floorZIndex(active)
        const blur = this.backgroundBlur(floor, active.floor)
        this.setBackgroundFilters(blur, this.groundShade && this.root.filters?.includes(this.groundShade)
            ? this.groundShade : undefined)
        if (frame.mapDeltas?.length) {
            this.map.queueMapDelta(frame.mapDeltas)
            this.map.rerender()
            if (this.root.visible || !this.map.flushMapDeltas) this.map.renderTileMap()
            else this.map.flushMapDeltas()
        }
        const present = new Set<string>()
        for (const message of frame.messages) {
            if (!message.position || message.entityType === "system") continue
            if (message.entityType === "bullet" && message.entityState === "dead") continue
            present.add(message.entityId)
            let item = this.entities.get(message.entityId)
            if (!item) {
                const entity = new Entity()
                Object.assign(entity, message)
                entity.location = Object.assign({ x: message.position[0], y: message.position[1] }, { roomId: frame.roomId })
                const sprite = this.createEntity(entity, this.provider)
                initializeEntityDepth(entity, sprite, this.provider)
                const overlay = this.overlays.offer(entity)
                sprite.setWorldOverlayItem(overlay)
                sprite.interactive = false
                sprite.interactiveChildren = false
                this.root.addChild(sprite)
                item = { entity, sprite, overlay, samples: [],
                    bulletTs: entity.entityType === "bullet" ? now : undefined }
                this.entities.set(message.entityId, item)
            }
            const { entity, sprite } = item
            // Stair playback uses current phase coordinates, so old delayed samples
            // must not pull the actor back toward the opening when the walk finishes.
            if (message.stairTravel || entity.stairTravel) {
                item.samples.length = 0
                item.renderPosition = undefined
            }
            Object.assign(entity, message)
            entity.floorFall = message.floorFall
            entity.stairTravel = message.stairTravel
            if (entity.entityType !== "bullet") {
                // Keep authoritative positions off the drawn entity between snapshots.
                const previous = item.samples[item.samples.length - 1]
                const sample = { ts: timestamp, x: message.position[0], y: message.position[1],
                    direction: message.direction, angle: message.angle }
                // Idle companion snapshots are suppressed between heartbeats. When
                // movement resumes, the prior position was still current at the last
                // 100ms server check; do not interpolate over the whole quiet period.
                if (!this.observation && previous && timestamp - previous.ts > 250) {
                    item.samples.splice(0, item.samples.length - 1)
                    previous.ts = timestamp - 100
                }
                if (previous && Math.hypot(sample.x - previous.x, sample.y - previous.y) > 4 * TileSize) {
                    item.samples.length = 0
                    item.renderPosition = undefined
                    entity.location = { x: sample.x, y: sample.y }
                }
                if (item.samples.length && item.samples[item.samples.length - 1].ts >= timestamp) item.samples.pop()
                item.samples.push(sample)
                if (item.samples.length > 32) item.samples.shift()
            }
            entity.movement.direction = message.direction
            entity.movement.angle = message.angle
            entity.roomId = frame.roomId
            entity.maxHp = message.hpMax
            item.visualDirty = true
            if (this.root.visible) {
                this.visuals.update(sprite, entity, this.features)
                item.visualDirty = false
            }
            sprite.visible = message.visible !== false && !item.bulletStopped
            const subject = sprite as ClientRenderable & Partial<GlintSubject>
            if (entity.npcType === "collectable" && subject.glintArt)
                this.overlays.offerDroppedItemGlint(entity.entityId, subject as GlintSubject)
            if (this.root.visible) this.overlays.updateEntity(entity)

        }
        this.entities.forEach((item, id) => {
            if (present.has(id)) return
            this.spawnEffects.get(id)?.destroy()
            this.spawnEffects.delete(id)
            this.completedSpawns.delete(id)
            item.sprite.parent?.removeChild(item.sprite)
            item.sprite.cleanup?.()
            item.sprite.destroy({ children: true })
            this.overlays.remove(item.entity)
            item.overlay.destroy({ children: true })
            this.entities.delete(id)
        })
    }

    /** The floor's root, with its sky layer above everything in it, added to the scene. Returns the sky. */
    createRoot(ground: boolean): Container {
        // The ground room contains the whole forest. Small upper rooms keep their
        // artwork bounds so their filters do not allocate a full viewport texture.
        this.root = ground ? new ViewFilteredContainer() : new Container()
        this.root.sortableChildren = true
        this.root.interactiveChildren = false
        const sky = new Container()
        sky.zIndex = 1000000
        this.root.addChild(sky)
        this.overlays = new WorldOverlaySurface(undefined, this.root, this.features)
        this.overlays.scale.set(1 / ScalingParams.surfaceZoom)
        this.overlays.zIndex = 2000000
        this.root.addChild(this.overlays)
        this.surface.addChild(this.root)
        return sky
    }

    /** Draw this floor with a map that already shows it (see SceneRoomRenderer.exchange). */
    adopt(provider: TileMapMetaProvider, map: SceneMap): void {
        this.roomId = provider.roomId
        this.provider = provider
        this.map = map
        this.adopted = true
    }

    /** Give up the map, without destroying what it drew, ahead of clear(). */
    releaseMap(): SceneMap | undefined {
        const map = this.map
        this.map = undefined
        return map
    }

    private backgroundBlur(floor: number, activeFloor: number): InstanceType<typeof filters.BlurFilter> | undefined {
        const distance = activeFloor - floor
        // Only the overworld softens; intermediate storeys stay sharp. Each blurred floor is a
        // full-view filter pass, so blurring every storey made each climb cost frames.
        if (this.observation || floor !== 0 || distance < 3) return undefined
        // Floors 1 and 2 stay sharp; floor 3 and above soften with a readability cap.
        // Distant artwork is already softened. Half-resolution filter targets
        // process a quarter of the pixels without reducing nearby-floor detail.
        // Pixi's blur strength uses logical coordinates, so its radius is unchanged.
        this.groundBlur ||= new filters.BlurFilter(0.375, 2, 0.5)
        this.groundBlur.blur = Math.min(1.125, 0.375 + (distance - 1) * 0.25)
        return this.groundBlur
    }

    private setBackgroundFilters(blur?: InstanceType<typeof filters.BlurFilter>, shade?: InstanceType<typeof filters.ColorMatrixFilter>): void {
        const current = this.root.filters
        const count = Number(!!blur) + Number(!!shade)
        if ((current?.length ?? 0) === count && (!blur || current[0] === blur) &&
            (!shade || current[count - 1] === shade)) return
        this.root.filters = blur ? shade ? [blur, shade] : [blur] : shade ? [shade] : null
    }

    private floorZIndex(active: TwoStoreyHouse): number {
        const house = this.provider.getHousePresentation()
        const floor = house.floor
        if (floor < active.floor) return -1000 + floor
        // A recessed or edited storey must sort at its own live southern edge.
        // Borrowing the active floor's extent puts northern upper tiles over
        // trees whose trunks stand in front of that storey.
        return sceneFloorGroundDepth(house.top + house.depth - 1, floor)
    }

    getEntity(id: string): Entity | undefined { return this.entities.get(id)?.entity }
    getRenderable(id: string): ClientRenderable | undefined { return this.entities.get(id)?.sprite }

    getLights(): LightsContainer {
        const lights = new LightsContainer()
        this.entities.forEach(({ entity }) => {
            if ((entity.lightMeta || entity.npcType === "light") && entity.visible !== false && !entity.isDead()) lights.set(entity.entityId,
                { entityId: entity.entityId, location: entity.location,
                    radius: entity.lightRadius(), intensity: entity.lightIntensity() })
        })
        return lights
    }

    /** A crumble, shake or repair on this storey plays on its own map, so it shows where the storey is drawn. */
    tileEffect(effect: TileEffect): void {
        if (!this.map || !this.provider) return
        if (effect.hp !== undefined && effect.hpMax !== undefined && (effect.effectType === "damage" || effect.effectType === "repair")) {
            this.provider.updateMapDeltaWithHP(effect.mapRoughCoordinates, effect.hp, effect.hpMax, effect.layerId)
        }
        this.map.tileEffect?.(effect)
    }

    projectileAim(point: Coordinate, active: TwoStoreyHouse): CrossFloorAim | undefined {
        if (!this.root?.visible || this.root.alpha <= .15 || !this.provider ||
            this.provider.twoStoreyHouse.floor === active.floor) return
        const localY = point.y - this.root.y
        // Only run on a trigger pull; passive scenes remain non-interactive.
        for (const { entity, sprite } of this.entities.values()) {
            if (!sprite.visible || entity.visible === false || entity.isDead() || entity.floorFall || entity.stairTravel ||
                !["player", "npc"].includes(entity.entityType) || !entity.strikeable) continue
            const bounds = entity.getBounds()
            // All LPC bodies share the same sole line, including small creatures whose
            // visible bodies sit below their simulation centre. Centre-based bounds miss
            // their feet and silently turn a downstairs shot into a same-floor shot.
            // Humanoids additionally need their full 25x50 body rather than 25x25 bounds.
            const humanoid = Mechanics.entity.body.isHumanoid(entity.appearance?.race)
            const body = entity.appearance?.race
                ? humanoid ? Mechanics.entity.body.dimensions(entity) : entity.dimensions
                : undefined
            const scale = Number.isFinite(entity.spriteScale) && entity.spriteScale > 0 ? entity.spriteScale : 1
            const x1 = body ? entity.location.x - body.w * scale / 2 : bounds.x1
            const x2 = body ? entity.location.x + body.w * scale / 2 : bounds.x2
            const y2 = body ? entity.location.y + BODY_GROUND_OFFSET : bounds.y2
            const y1 = body ? y2 - body.h * scale : bounds.y1
            if (point.x >= x1 && point.x <= x2 && localY >= y1 && localY <= y2 &&
                !this.provider.isUnderRoofAt(entity.location))
                return { roomId: this.roomId, x: entity.location.x, y: entity.location.y }
        }
    }

    render(active: TwoStoreyHouse | undefined, player: Entity): void {
        if (this.observation) active = this.provider?.getHousePresentation()
        if (!this.root || !active || !player?.location) return
        if (this.observation) this.map?.setViewEntity?.(player)
        this.root.y = this.observation ? 0 : companionFloorOffset(active, this.provider.twoStoreyHouse)
        const floor = this.provider.twoStoreyHouse.floor
        this.root.zIndex = this.floorZIndex(active)
        const enclosed = !isExteriorFloorFall(player.floorFall) && insideHouse(active, player.location)
        // The overworld is the backdrop around the tower, visible at any height; deeper storeys
        // still supply collision and floor support while hidden.
        const inView = floor === 0 || active.floor - floor <= MAX_VISIBLE_FLOORS_BELOW
        this.visibilityFade.update(this.observation || (floor < active.floor ? inView : !enclosed))
        this.root.alpha = this.visibilityFade.alpha
        this.root.visible = this.visibilityFade.visible
        const visible = this.root.visible
        const blur = visible ? this.backgroundBlur(floor, active.floor) : undefined
        if (!this.observation && floor < active.floor) {
            if (enclosed) {
                this.groundShade ||= new filters.ColorMatrixFilter()
                // Keep distant rooms readable while deepening the shade per storey.
                const brightness = Math.max(0.5, Math.pow(0.88, active.floor - floor))
                if (brightness !== this.groundBrightness) {
                    this.groundShade.brightness(brightness, false)
                    this.groundBrightness = brightness
                }
                this.setBackgroundFilters(blur, this.groundShade)
            } else {
                this.setBackgroundFilters(blur)
            }
        } else {
            this.setBackgroundFilters()
        }
        this.map?.setProjectionFloor?.(active.floor)
        // Hidden maps still supply collision and floor support to visible neighbours.
        // Keep deltas applied, while leaving the requested paint pending until reveal.
        if (visible || !this.map?.flushMapDeltas) this.map?.renderTileMap()
        else this.map.flushMapDeltas()
        this.map?.updateTileEffects?.()
        // Match the active room's playback delay, covering the companion's 100ms snapshot gap.
        const timestamp = Date.now() - (this.clockOffset ?? 0) - 150
        const now = Date.now()
        this.entities.forEach(item => {
            const { entity, sprite, overlay, samples } = item
            if (entity.entityType === "bullet") {
                // Match active-floor bullets: integrate every frame from the first
                // received position, rather than waiting on delayed snapshots. Keep
                // a locally finished bullet hidden until its authoritative removal.
                const dt = Math.max(0, now - (item.bulletTs ?? now)) / 1000
                item.bulletTs = now
                if (!item.bulletStopped && (entity.speed2.x || entity.speed2.y))
                    item.bulletStopped = !advanceProjectile(entity, dt, this.provider)
                sprite.visible = entity.visible !== false && !item.bulletStopped
            } else {
                while (samples.length >= 2 && samples[1].ts <= timestamp) samples.shift()
                const from = samples[0]
                if (!from) return
                const to = samples[1]
                const progress = to && timestamp >= from.ts
                    ? Math.min(1, (timestamp - from.ts) / (to.ts - from.ts)) : 0
                // NPC brains move in discrete turns, so snapshots can repeat a position before
                // jumping ahead. Match active-floor smoothing to bridge those short pauses.
                const x = from.x + ((to?.x ?? from.x) - from.x) * progress
                const y = from.y + ((to?.y ?? from.y) - from.y) * progress
                if (visible) {
                    item.renderPosition = smoothRenderPosition(item.renderPosition, x, y, now)
                    entity.location.x = item.renderPosition.x
                    entity.location.y = item.renderPosition.y
                } else {
                    entity.location.x = x
                    entity.location.y = y
                }
                const facing = progress > 0 ? to : from
                entity.movement.direction = facing.direction
                entity.movement.angle = facing.angle
            }
            if (!visible) {
                // Resume from the current playback position instead of easing from a
                // stale hidden sprite. Projectiles and vertical travel keep stepping.
                item.renderPosition = undefined
                if (entity.entityType !== "bullet" && !entity.stairTravel && !entity.floorFall) return
            }
            if (visible && item.visualDirty) {
                this.visuals.update(sprite, entity, this.features, { role: this.getRole(entity.entityId) })
                item.visualDirty = false
            }
            // Roster updates can arrive without a new room snapshot.
            if (visible) this.visuals.updateRole(sprite, this.getRole(entity.entityId))
            // The local player's two halves share predicted input, not two network clocks.
            if (entity.stairTravel?.sourceEntityId === player.entityId && entity.stairTravel?.projection) {
                const view = stairTravelInRoom(player.stairTravel, this.roomId)
                if (!view || player.roomId === this.roomId) {
                    sprite.visible = overlay.visible = false
                    return
                }
                entity.stairTravel = { ...view, sourceEntityId: player.entityId }
                entity.location = { ...player.location, ...stairTravelPose(view, 0).location }
                sprite.visible = entity.visible !== false
            }
            renderEntityFrame(entity, sprite, this.roomId, visible, now)
            updatePreSpawnEffect(entity, sprite, this.root, this.spawnEffects, this.completedSpawns)
            this.overlays.updateEntity(entity)
            overlay.visible = sprite.visible && !entity.stairTravel?.projection

        })
        sweepPreSpawnEffects(this.spawnEffects, this.completedSpawns, id => visible && this.entities.get(id)?.sprite.visible)
        if (visible) {
            this.overlays.animateDroppedItemGlints(now)
            this.overlays.animateBonecallerWarnings()
        }
    }
}

/** Each passive floor retains its own artwork and movement buffer. */
export class SceneRoomRenderer {
    private floors = new Map<string, SceneFloorRenderer>()
    private backgroundArtwork = { key: "", composites: [] as ProjectedComposite[] }
    // The first companion remains accessible for map previews and diagnostics.
    get root(): Container | undefined { return this.floors.values().next().value?.root }
    get map(): SceneMap | undefined { return this.floors.values().next().value?.map }

    backgroundCompositeArtwork(active?: TwoStoreyHouse): { key: string; composites: ProjectedComposite[] } {
        const versions: string[] = []
        for (const [id, floor] of this.floors) {
            if (!active || !floor.root || floor.provider?.twoStoreyHouse?.floor >= active.floor) continue
            versions.push(`${id}:${floor.map?.getArtworkVersion?.()}:${floor.root.x}:${floor.root.y}`)
        }
        const key = versions.join("|")
        if (key !== this.backgroundArtwork.key) {
            const composites: ProjectedComposite[] = []
            for (const floor of this.floors.values()) {
                if (!active || !floor.root || floor.provider?.twoStoreyHouse?.floor >= active.floor) continue
                floor.map?.getCompositeSprites?.().forEach(source => composites.push({ source, projection: floor.root }))
            }
            this.backgroundArtwork = { key, composites }
        }
        return this.backgroundArtwork
    }

    constructor(private surface: Container, private createMap: (root: Container, sky: Container) => SceneMap,
        private createEntity: (entity: Entity, provider?: TileMapMetaProvider) => ClientRenderable,
        private retainEntity?: (entity: Entity, sprite: ClientRenderable) => boolean,
        private getRole: (id: string) => EmberRosterRole | null = () => null) {}

    clear(): void {
        this.floors.forEach(floor => floor.clear())
        this.floors.clear()
        this.backgroundArtwork = { key: "", composites: [] }
    }

    floorProvider(roomId: string): TileMapMetaProvider | undefined {
        const floor = this.floors.get(roomId)
        return floor?.map && floor.provider?.roomId === roomId ? floor.provider : undefined
    }

    /** The sprites drawing one tile of a floor on screen (see MapRenderingManager.getTileArt). */
    tileArt(roomId: string, x: number, y: number, layerId: string): Sprite[] {
        const floor = this.floors.get(roomId)
        return floor?.root?.visible ? floor.map?.getTileArt?.(x, y, layerId) ?? [] : []
    }

    floorMap(roomId: string): SceneMap | undefined {
        const floor = this.floors.get(roomId)
        return floor?.provider?.roomId === roomId ? floor.map : undefined
    }

    /**
     * The player changed level to `destinationId`, a floor drawn here, from `leaving`, drawn by the
     * active map. `swap` hands the destination's map the active role and gives the active map the new
     * root made here for the floor being left; nothing is repainted. The destination's bodies are kept
     * for the active entity manager, as activateRoom does.
     */
    exchange(destinationId: string, leaving: TileMapMetaProvider, active: SceneMap,
        swap: (root: Container, sky: Container) => void): boolean {
        const destination = this.floors.get(destinationId)
        if (!destination?.map || !leaving.roomId) return false
        // A late snapshot can leave an entry for the active room; it never held this floor's drawing.
        this.floors.get(leaving.roomId)?.clear()
        this.floors.delete(leaving.roomId)
        const floor = new SceneFloorRenderer(this.surface, this.createMap, this.createEntity, false, this.retainEntity, this.getRole)
        const sky = floor.createRoot(leaving.twoStoreyHouse?.floor === 0)
        swap(floor.root, sky)
        floor.adopt(leaving, active)
        destination.releaseMap()
        destination.clear()
        this.floors.delete(destinationId)
        this.floors.set(leaving.roomId, floor)
        return true
    }

    activateRoom(roomId: string): void {
        // This floor will now be drawn by the active entity manager. Retire its
        // passive map while handing its loaded bodies to that manager.
        this.floors.get(roomId)?.clear()
        this.floors.delete(roomId)
    }

    getEntity(id: string): Entity | undefined {
        for (const floor of this.floors.values()) {
            const entity = floor.getEntity(id)
            if (entity) return entity
        }
    }

    getRenderable(id: string): ClientRenderable | undefined {
        for (const floor of this.floors.values()) {
            const sprite = floor.getRenderable(id)
            if (sprite) return sprite
        }
    }

    projectileAim(point: Coordinate, active?: TwoStoreyHouse): CrossFloorAim | undefined {
        if (!point || !active) return
        // Prefer the highest drawn storey when bodies from different floors overlap.
        let aim: CrossFloorAim | undefined, highest = -1
        for (const floor of this.floors.values()) {
            const candidate = floor.projectileAim(point, active)
            if (candidate && floor.provider.twoStoreyHouse.floor > highest) {
                aim = candidate
                highest = floor.provider.twoStoreyHouse.floor
            }
        }
        return aim
    }

    /** Empty ground can receive a descending shot too; pick the highest visible walking surface. */
    surfaceAim(point: Coordinate, active?: TwoStoreyHouse): CrossFloorAim | undefined {
        if (!point || !active || active.floor <= 0) return
        const x = Math.floor(point.x / TileSize), y = Math.floor(point.y / TileSize)
        if (active.floorCells.some(cell => cell.x === x && cell.y === y)) return
        let selected: CrossFloorAim | undefined, highest = -1
        for (const floor of this.floors.values()) {
            const provider = floor.provider, root = floor.root
            const level = provider?.twoStoreyHouse?.floor
            if (!root?.visible || root.alpha <= .15 || level === undefined || level >= active.floor || level <= highest) continue
            const local = { x: point.x, y: point.y - root.y }
            const cell = { x: Math.floor(local.x / TileSize), y: Math.floor(local.y / TileSize) }
            if (cell.x < 0 || cell.y < 0 || cell.x >= provider.width() || cell.y >= provider.height()) continue
            const tile = provider.resolveTileMapFromLayerId("map2")?.[cell.x]?.[cell.y]
            if (level > 0 && (!tile || tile === "0" || provider.isFloorOpeningAt(cell.x, cell.y))) continue
            if (provider.isUnderRoofAt({ x: local.x, y: local.y - BODY_GROUND_OFFSET })) continue
            selected = { roomId: provider.roomId, ...local, surface: true }
            highest = level
        }
        return selected
    }

    tileEffect(effect: TileEffect): void {
        if (effect.roomId) this.floors.get(effect.roomId)?.tileEffect(effect)
    }

    /** The other storeys on screen now, each where it is drawn, so a press can pick their tiles. */
    drawnStoreys(active?: TwoStoreyHouse): DrawnStorey[] {
        const storeys: DrawnStorey[] = []
        if (!active) return storeys
        this.floors.forEach((floor, roomId) => {
            const level = floor.provider?.twoStoreyHouse?.floor
            if (!floor.root?.visible || floor.root.alpha <= .15 || level === undefined || level === active.floor) return
            storeys.push({ roomId, level, yOffset: floor.root.y, map: floor.provider,
                roofVisible: floor.map.isRoofVisibleForPlacement?.() ?? true })
        })
        return storeys
    }

    getLights(): Light[] {
        const lights: Light[] = []
        this.floors.forEach(floor => {
            if (!floor.root?.visible || floor.root.alpha <= 0) return
            floor.getLights().iterate(light => lights.push({ ...light,
                location: { x: light.location.x, y: light.location.y + floor.root.y },
                intensity: light.intensity * floor.root.alpha }))
        })
        return lights
    }

    update(frames: SceneRoomState[], active?: TwoStoreyHouse): void {
        if (!active || !frames.length) { this.clear(); return }
        const allowed = active.floorRoomIds ?? [active.groundRoomId, active.upperRoomId]
        this.floors.forEach((floor, id) => {
            if (allowed.includes(id)) return
            floor.clear()
            this.floors.delete(id)
        })
        for (const frame of frames) {
            let floor = this.floors.get(frame.roomId)
            if (!floor) {
                floor = new SceneFloorRenderer(this.surface, this.createMap, this.createEntity, false, this.retainEntity, this.getRole)
                this.floors.set(frame.roomId, floor)
            }
            floor.update([frame], active)
        }
    }

    hasForegroundFloor(playerZ: number): boolean {
        for (const floor of this.floors.values()) {
            if (floor.root?.visible && floor.root.alpha > 0.15 && floor.root.zIndex > playerZ) return true
        }
        return false
    }

    /** Bounds are in screen space, like the drawn player frame. Lower floors cannot cover it. */
    occludes(bounds: Rectangle, playerZ: number): boolean {
        for (const floor of this.floors.values()) {
            if (floor.root?.visible && floor.root.alpha > 0.15 && floor.root.zIndex > playerZ &&
                floor.map?.overlapsRenderedMap?.(bounds)) return true
        }
        return false
    }

    linkFloorSupport(active: TileMapMetaProvider): void {
        const providers = new Map<string, TileMapMetaProvider>([[active.roomId, active]])
        this.floors.forEach((floor, id) => { if (floor.provider) providers.set(id, floor.provider) })
        providers.forEach(provider => {
            const house = provider.twoStoreyHouse
            if (!house) return
            provider.setUpperFloorProvider(providers.get(house.floorRoomIds?.[house.floor + 1] ??
                (house.floor === 0 ? house.upperRoomId : undefined)))
            if (house.floor <= 0) return
            const below = house.floorRoomIds?.[house.floor - 1] ?? house.groundRoomId
            provider.setFloorSupportProvider(providers.get(below))
        })
    }

    render(active: TwoStoreyHouse | undefined, player: Entity): void {
        this.floors.forEach(floor => floor.render(active, player))
    }
}
